import styled from 'styled-components'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Avatar,
  ButtonIcon,
  IconHeart,
  IconHeartActive,
  IconDownload,
  MenuRenderer,
  MenuItem,
  IconMoreVert,
  Text,
  Heading
} from '@machobear/design-system'
import ENV_VARS from '@/config'
import client from '@/ports/api-client'
import VerifiedBadge from '@/components/verified-badge'
import Skeleton from '@/components/skeleton'
import formatName from '@/utils/format-name'
import isVerified from '@/utils/is-verified'
import useMediaQuery from '@/hooks/use-media-query'
import { toast } from '@/components/toast-notifications'
import { showTenantVerificationModal } from '@/features/Chat/tenant-verification-modal'
const { BACKEND_SERVER } = ENV_VARS

const HeartWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
`

type ApplicantInfoType = {
  data: {
    first_name: string
    last_name: string
    updated_at: string
  }
  applicationDate: string
  showFullName?: boolean
  isFavourite?: boolean
  loading: boolean
  reportId: string | number
}

const ApplicantInfo = ({
  data,
  showFullName = true,
  isFavourite = false,
  loading,
  reportId
}: ApplicantInfoType) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await client.put(`/trust_score/standalone/${reportId}/favourite`)
      return response
    },
    onError: () => {
      toast(t('l.standalone.view.favourites_error_text.text', 'Error toggling favourite.'), {
        title: `Error`,
        type: 'error'
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['standalone', reportId] })
      queryClient.invalidateQueries({ queryKey: ['trustScoreList'] })
      toast(
        t(
          'l.standalone.view.favourites_success_text.text',
          'To view your favourite reports, please go to the Trust Score page.'
        ),
        {
          title: `${data?.first_name} ${
            isFavourite
              ? t(
                  'l.application.view.remove_from_favourites_success.title',
                  'removed from favourites'
                )
              : t('l.application.view.add_to_favourites_success.title', 'added to favourites')
          }`,
          type: 'success'
        }
      )
    }
  })

  const handleFavourite = async () => {
    mutation.mutate()
  }

  const formattedDate = moment(data?.updated_at).format('MM-DD-YYYY')
  const isMobile = useMediaQuery(`(max-width: 991px)`)
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language
  const isNotEn = currentLanguage === 'zh'

  const formatFullName = (data: { first_name: string; last_name: string }) => {
    return isVerified(data, null) && data?.first_name.length + data?.last_name.length >= 13 ? (
      <span>
        {`${data?.first_name}`} <br /> {`${data?.last_name}`}
      </span>
    ) : (
      `${data?.first_name} ${data?.last_name}`
    )
  }

  const pdfUrl = `${BACKEND_SERVER}/trust_score/standalone/${reportId}/application.pdf${
    isNotEn ? '?locale=zh-CN' : ''
  }`

  return (
    <Box
      padding="medium"
      background="white"
      boxShadow="xsmall"
      borderRadius="standard"
      display="flex"
      flexDirection={{ mobile: 'row', tablet: 'column' }}
      gap="gutter"
    >
      <Box position="relative" cursor="pointer">
        {loading ? (
          <Skeleton width={isMobile ? 80 : 245} height={isMobile ? 80 : 187} />
        ) : (
          <Box style={{ width: isMobile ? '80px' : '245px', height: isMobile ? '80px' : '190px' }}>
            <Avatar
              img={null}
              size={isMobile ? 'medium' : 'full'}
              shape={'rounded'}
              name={data?.first_name}
            />
          </Box>
        )}
        {!isMobile && !loading && (
          <HeartWrapper>
            <ButtonIcon
              id="application-trust-score-sidebar-add-fav"
              icon={
                isFavourite ? <IconHeartActive tone="brandAccent" /> : <IconHeart tone="neutral" />
              }
              size="large"
              label={
                isFavourite
                  ? t('b.chat.view.remove_from_favourites.button', 'Remove from favourites')
                  : t('b.chat.view.add_to_favourites.button', 'Add to favourites')
              }
              onClick={handleFavourite}
            />
          </HeartWrapper>
        )}
      </Box>
      <Box display="flex" flexDirection="column" width="full" gap="medium">
        <Box display="flex" justifyContent="spaceBetween" alignItems="center">
          {loading ? (
            <Skeleton width="80%" height={20} />
          ) : (
            <Box style={{ maxWidth: isMobile ? '100%' : '215px' }}>
              <Heading level="4">
                {!showFullName ? formatName(data) : formatFullName(data)}
                <Box
                  display="inline"
                  cursor="pointer"
                  onClick={() =>
                    showTenantVerificationModal({
                      verificationStatus: 'verified',
                      timelineRoomId: null
                    })
                  }
                >
                  <VerifiedBadge
                    type="id"
                    width={30}
                    height={26}
                    left={5}
                    bottom={-4}
                    verified={isVerified(data, null)}
                  />
                </Box>
              </Heading>
            </Box>
          )}

          <MenuRenderer
            offsetSpace="xxsmall"
            menuPosition="absolute"
            trigger={triggerProps => (
              <Box userSelect="none" cursor="pointer" {...triggerProps}>
                <IconMoreVert size="large" />
              </Box>
            )}
          >
            <MenuItem
              onClick={handleFavourite}
              icon={isFavourite ? <IconHeartActive tone="neutral" /> : <IconHeart tone="neutral" />}
            >
              Add to favourites
            </MenuItem>
            <MenuItem
              onClick={() => window.open(pdfUrl, '_blank')}
              icon={<IconDownload tone="neutral" />}
            >
              Export as PDF
            </MenuItem>
          </MenuRenderer>
        </Box>
        {loading ? (
          <Skeleton width="75%" height={20} />
        ) : (
          <Text size="small" tone="tertiary">{`${t(
            'l.standalone.view.last_updated.text',
            'Last updated'
          )}: ${formattedDate}`}</Text>
        )}
      </Box>
    </Box>
  )
}

export default ApplicantInfo
